import { makeStyles } from 'tss-react/mui';

import { SxProps, Theme } from '@mui/material';

/**
 * The paper of a dropdown (select menu, autocomplete list, menu), dark like the app's other
 * dropdowns (FormSelectField, TablePaginationContainer); the default paper is white, which
 * hides the white text.
 */
export const DROPDOWN_PAPER_SX: SxProps<Theme> = {
  backgroundColor: 'secondary.dark',
  color: 'common.white',
  '& .MuiMenuItem-root, & .MuiAutocomplete-option, & .MuiAutocomplete-noOptions': {
    fontFamily: 'Inter',
    fontSize: '14px',
  },
  '& .MuiAutocomplete-noOptions': { color: 'text.secondary' },
  '& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover, & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"]':
    { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
};

export const useActivityStyles = makeStyles()((theme: Theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '8px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
  },
  tabs: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    padding: '4px',
    gap: '4px',
    borderRadius: '10px',
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
  },
  tab: {
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    minWidth: 0,
    '&:hover': { background: theme.palette.secondary.light },
  },
  tabActive: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  search: {
    flex: '1 1 280px',
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      background: theme.palette.primary.light,
      borderRadius: '10px',
      height: '42px',
      '& fieldset': { borderColor: theme.palette.secondary.dark },
      '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    },
    '& svg': { color: theme.palette.text.secondary },
  },
  toolbarButton: {
    height: '42px',
    padding: '0 16px',
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 500,
    color: theme.palette.common.white,
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    '&:hover': { background: theme.palette.secondary.light },
  },
  toolbarButtonActive: {
    background: theme.palette.secondary.light,
  },
  counts: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.palette.text.secondary,
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
  failurePill: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '12px',
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    background: 'rgba(255, 134, 99, 0.12)',
  },
  panel: {
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    padding: '16px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '16px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  fieldLabel: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  input: {
    width: '100%',
    '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
      color: theme.palette.common.white,
      background: theme.palette.primary.main,
      borderRadius: '8px',
      '& fieldset': { borderColor: theme.palette.secondary.dark },
    },
    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
    '& svg': { color: theme.palette.text.secondary },
  },
  switchBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    minHeight: '40px',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    fontSize: '14px',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '4px',
  },
  chip: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.secondary.dark,
    fontSize: '12px',
    height: '26px',
  },
  chipActive: {
    color: theme.palette.common.white,
    background: `${theme.palette.info.main} !important`,
    borderColor: theme.palette.info.main,
  },
  // The table looks like the app's other tables (components/Table: TableContainer, TableHeader,
  // TablePaginationContainer); its header is the shared TableHeader.
  tableContainer: {
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    paddingBottom: '10px',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableBody: {
    background: theme.palette.primary.light,
    '& .MuiTableRow-root': {
      '&:hover': {
        cursor: 'pointer',
        background: theme.palette.secondary.light,
      },
    },
    '& .MuiTableCell-root': {
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontStyle: 'normal',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      lineHeight: '16px',
      height: '65px',
      padding: 0,
      paddingLeft: '16px',
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // The activity's title and tags read in full.
  activityCell: {
    '&.MuiTableCell-root': { maxWidth: '480px' },
  },
  failedRow: {
    background: 'rgba(255, 134, 99, 0.08)',
    boxShadow: `inset 3px 0 0 ${theme.palette.error.main}`,
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    marginTop: '2px',
  },
  time: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  strong: {
    fontWeight: 600,
  },
  personLink: {
    fontWeight: 600,
    textAlign: 'left',
  },
  entityPill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '12px',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.secondary.dark}`,
    whiteSpace: 'nowrap',
  },
  categoryTag: {
    marginLeft: '8px',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.secondary.dark}`,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  failedBadge: {
    marginLeft: '8px',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    background: 'rgba(255, 134, 99, 0.12)',
    verticalAlign: 'middle',
  },
  footerNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: theme.palette.text.secondary,
    fontSize: '12px',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  // Person timeline
  backLink: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    color: theme.palette.common.white,
  },
  headerCard: {
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    padding: '20px',
  },
  personName: {
    fontSize: '22px',
    fontWeight: 600,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  statLabel: {
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  statValue: {
    marginTop: '6px',
    fontWeight: 600,
  },
  sessionCard: {
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  sessionHeader: {
    display: 'flex',
    gap: '8px',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    color: theme.palette.text.secondary,
    fontSize: '13px',
  },
  sessionDate: {
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  timelineRow: {
    display: 'grid',
    gridTemplateColumns: '80px 16px 1fr auto',
    alignItems: 'start',
    gap: '8px',
    padding: '10px 16px',
  },
  timelineDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '5px',
    background: theme.palette.info.main,
  },
  timelineDotFailed: {
    background: theme.palette.error.main,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': { background: theme.palette.secondary.light },
  },
  detailsLink: {
    marginLeft: '6px',
    whiteSpace: 'nowrap',
    color: '#9CB0C1', // palette info.extralight (not in the MUI palette type)
    textDecoration: 'underline',
  },
  // Event detail drawer
  drawer: {
    width: '520px',
    maxWidth: '100vw',
    minHeight: '100%',
    background: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    fontFamily: 'Inter',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: '8px',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    rowGap: '8px',
    columnGap: '12px',
    margin: 0,
    fontSize: '13px',
    '& > dt': { color: theme.palette.text.secondary },
    '& > dd': { margin: 0, wordBreak: 'break-word' },
  },
  changesTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    '& th': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      fontWeight: 500,
      padding: '6px 8px',
    },
    '& td': {
      padding: '8px',
      borderTop: `1px solid ${theme.palette.secondary.dark}`,
      verticalAlign: 'top',
      wordBreak: 'break-word',
    },
  },
  omitted: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));
